import React from 'react';
import { createPortal } from 'react-dom';
import { Typography, Button, Avatar, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { IconUserCircle, IconMapPin, IconStarFilled, IconRosetteDiscountCheckFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { INearbyOperator } from '../interfaces/heavyDutyMachinery/IService';
import { TEAL, SURFACE_1_SOFT } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import GearLoadingState from './GearLoadingState';

const { Text, Title } = Typography;

export interface INearbyOperatorsSheetProps {
  open: boolean;
  operators: INearbyOperator[];
  loading: boolean;
  onClose: () => void;
  /**
   * What a row can do here, which is decided entirely by whether the
   * machinery rental is already secured:
   *
   * 'browse' -- it is not. Look, compare, close. Hiring somebody at this
   *   point could leave the guest paying a person for a job that never
   *   happens, because the host has not accepted the rental yet.
   * 'hire' -- it is. Rows lead to that operator's own booking flow.
   */
  mode: 'browse' | 'hire';
  /** Only called in 'hire' mode. */
  onHire?: (operator: INearbyOperator) => void;
}

/**
 * Full-screen list of operators working near a job.
 *
 * Opens OVER whatever screen called it -- checkout included -- and closes
 * back onto it, which is the whole reason it is a sheet and not a page: a
 * guest halfway through a booking must never lose the form to go and look
 * at who is around. Same portal + framer-motion mechanics as
 * AccessoryPickerSheet, and the same teal accent, since this is guest
 * checkout territory rather than the host-facing coral scope.
 *
 * Everybody in here is hired SEPARATELY from the machine (see
 * INearbyOperator's own doc comment) -- they are their own person with
 * their own calendar, not a line on somebody else's rental. The sheet says
 * so out loud rather than leaving the guest to discover it at the end.
 */
const NearbyOperatorsSheet: React.FC<INearbyOperatorsSheetProps> = ({ open, operators, loading, onClose, mode, onHire }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const priceLabel = (operator: INearbyOperator) =>
    operator.pricingMode === 'FixedJob'
      ? formatCurrency(operator.fixedJobPrice ?? 0, operator.hostCurrencyCode, i18n.language)
      : `${formatCurrency(operator.hourlyRate ?? 0, operator.hostCurrencyCode, i18n.language)}${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_HOUR)}`;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="nearby-operators-sheet"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.28, ease: 'easeInOut' }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2200,
            background: token.colorBgContainer,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              padding: '16px 16px 12px',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <Title level={5} style={{ margin: 0 }}>
                {translate(t, TRANSLATIONS.NEARBY_OPERATORS_TITLE)}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {translate(t, mode === 'hire' ? TRANSLATIONS.NEARBY_OPERATORS_SUBTITLE_HIRE : TRANSLATIONS.NEARBY_OPERATORS_SUBTITLE_BROWSE)}
              </Text>
            </div>
            <Button type="text" shape="circle" icon={<CloseOutlined />} onClick={onClose} aria-label={translate(t, TRANSLATIONS.NEARBY_OPERATORS_CLOSE)} />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {loading ? (
              <GearLoadingState message={translate(t, TRANSLATIONS.NEARBY_OPERATORS_LOADING)} />
            ) : (
              operators.map((operator) => {
                const isUnavailable = !operator.isAvailableForRequestedDates;
                return (
                  <div
                    key={operator.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      padding: '14px 0',
                      borderBottom: `1px solid ${token.colorBorderSecondary}`,
                      // Busy on these dates stays in the list, greyed, rather
                      // than being dropped -- same "shown disabled, not
                      // hidden" treatment the operator and accessory rows in
                      // checkout already use, so it reads as the same state
                      // everywhere in the app.
                      opacity: isUnavailable ? 0.55 : 1,
                    }}
                  >
                    <Avatar
                      size={44}
                      src={operator.providerProfilePhotoUrl || undefined}
                      icon={<IconUserCircle size={24} />}
                      style={{ flexShrink: 0, background: SURFACE_1_SOFT, color: token.colorTextSecondary }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Text strong style={{ minWidth: 0 }} ellipsis>
                          {operator.providerDisplayName}
                        </Text>
                        {operator.providerVerifiedBadge && (
                          <span style={{ display: 'flex', flexShrink: 0, color: TEAL }}>
                            <IconRosetteDiscountCheckFilled size={15} />
                          </span>
                        )}
                      </div>
                      <Text type="secondary" style={{ fontSize: 13, display: 'block' }} ellipsis>
                        {operator.title}
                      </Text>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: token.colorTextTertiary }}>
                          <IconMapPin size={13} />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {operator.city
                              ? `${operator.city} · ${translate(t, TRANSLATIONS.NEARBY_OPERATORS_DISTANCE, { km: operator.distanceKm })}`
                              : translate(t, TRANSLATIONS.NEARBY_OPERATORS_DISTANCE, { km: operator.distanceKm })}
                          </Text>
                        </span>
                        {operator.providerReviewCount > 0 && operator.providerAggregatedRating != null && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: token.colorTextTertiary }}>
                            <IconStarFilled size={12} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {operator.providerAggregatedRating.toFixed(1)} ({operator.providerReviewCount})
                            </Text>
                          </span>
                        )}
                      </div>

                      {isUnavailable && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          {translate(t, TRANSLATIONS.NEARBY_OPERATORS_UNAVAILABLE)}
                        </Text>
                      )}
                      {/* A deposit is not an obstacle here the way it is on an
                          attached Service -- booked on its own is exactly
                          where it works -- so it is stated, not used to
                          disable the row. */}
                      {!!operator.depositAmount && operator.depositAmount > 0 && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {translate(t, TRANSLATIONS.NEARBY_OPERATORS_DEPOSIT_NOTE, {
                            amount: formatCurrency(operator.depositAmount, operator.hostCurrencyCode, i18n.language),
                          })}
                        </Text>
                      )}
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <Text strong style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {priceLabel(operator)}
                      </Text>
                      {mode === 'hire' && (
                        <div style={{ marginTop: 6 }}>
                          <Button size="small" type="primary" disabled={isUnavailable} onClick={() => onHire?.(operator)}>
                            {translate(t, TRANSLATIONS.NEARBY_OPERATORS_HIRE_CTA)}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Said once, at the bottom, in both modes: this person is not
                part of the machinery booking and never will be. In 'browse'
                it also explains why there is no button to press yet. */}
            {!loading && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 16 }}>
                {translate(t, mode === 'hire' ? TRANSLATIONS.NEARBY_OPERATORS_HIRE_NOTE : TRANSLATIONS.NEARBY_OPERATORS_BROWSE_NOTE)}
              </Text>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default NearbyOperatorsSheet;
